// Unread-message counting for the chat badge (audit P2).
//
// The old badge compared array LENGTHS (seen count vs current count). The
// server projects a sliding window (last 80 per channel), so once a channel
// caps, the length stops growing and new messages no longer bumped the badge
// — and on a reset the length could even shrink below the seen count. Count
// by message IDENTITY instead: everything after the last message the user
// actually saw is unread; if that message has slid out of the window, fall
// back to its timestamp.

import type { ChatMessage } from '../../shared/src/types.js';

/** Identity of the newest message the user has seen in a channel. */
export interface SeenMark {
  id: string;
  ts: number;
}

type MsgLike = Pick<ChatMessage, 'id' | 'ts'>;

/** Mark for "the user has read this channel as of now" — the identity of the
 *  newest message, or null when the channel is empty (nothing to mark). */
export function lastSeenMark(messages: readonly MsgLike[] | undefined): SeenMark | null {
  if (!messages || messages.length === 0) return null;
  const last = messages[messages.length - 1];
  return { id: last.id, ts: last.ts };
}

/** Number of messages in the projected window newer than `seen`. */
export function countUnread(
  messages: readonly MsgLike[] | undefined,
  seen: SeenMark | undefined,
): number {
  if (!messages || messages.length === 0) return 0;
  if (!seen) return messages.length;
  // Scan from the tail — the seen message is usually near the end.
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].id === seen.id) return messages.length - 1 - i;
  }
  // The seen message slid out of the 80-message window (or the room's chat
  // was reset) — count by timestamp instead of declaring everything unread.
  let n = 0;
  for (const m of messages) if (m.ts > seen.ts) n++;
  return n;
}
